import { useState, useRef } from 'react';
import Button from '../Button';
import { submitWholesaleApplication } from '../../services/api';

const INITIAL = {
  business_name: '',
  contact_name: '',
  email: '',
  phone: '',
  address: '',
  amazon_store_url: '',
  message: '',
};

function validateEmail(val) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val.trim());
}

function validate(fields) {
  const errors = {};
  if (!fields.business_name.trim()) errors.business_name = 'Business name is required.';
  if (!fields.contact_name.trim()) errors.contact_name = 'Contact name is required.';
  if (!fields.email.trim()) {
    errors.email = 'Email is required.';
  } else if (!validateEmail(fields.email)) {
    errors.email = 'Enter a valid email address.';
  }
  if (!fields.phone.trim()) errors.phone = 'Phone number is required.';
  if (!fields.address.trim()) errors.address = 'Address is required.';
  return errors;
}

const CERTIFICATE_MIME_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/jpg',
  'image/png',
];

function validateCertificate(file) {
  if (!file) return 'Resell certificate is required.';
  if (!CERTIFICATE_MIME_TYPES.includes(file.type)) {
    return 'File must be a PDF or image (JPG, JPEG, PNG).';
  }
  if (file.size > 5 * 1024 * 1024) return 'File must be 5 MB or smaller.';
  return null;
}

/**
 * WholesaleForm
 *
 * Controlled form that posts to POST /wholesale-application.
 * Props:
 *   onSuccess — optional callback after successful submission
 */
function WholesaleForm({ onSuccess }) {
  const [fields, setFields] = useState(INITIAL);
  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [certificateFile, setCertificate] = useState(null);
  const certificateRef = useRef(null);

  function handleChange(e) {
    const { name, value } = e.target;
    setFields((prev) => ({ ...prev, [name]: value }));
    // Clear field-level error on change if field was touched
    if (touched[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  }

  function handleBlur(e) {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    const fieldErrors = validate(fields);
    if (fieldErrors[name]) {
      setErrors((prev) => ({ ...prev, [name]: fieldErrors[name] }));
    } else {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  }

  function handleCertificateChange(e) {
    const file = e.target.files[0] || null;
    setCertificate(file);
    if (touched.certificate) {
      const certErr = validateCertificate(file);
      setErrors((prev) => {
        const next = { ...prev };
        if (certErr) {
          next.certificate = certErr;
        } else {
          delete next.certificate;
        }
        return next;
      });
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    // Mark all required fields as touched (including certificate)
    setTouched({ business_name: true, contact_name: true, email: true, phone: true, address: true, certificate: true });
    const fieldErrors = validate(fields);
    const certErr = validateCertificate(certificateFile);
    if (certErr) fieldErrors.certificate = certErr;
    setErrors(fieldErrors);
    if (Object.keys(fieldErrors).length > 0) return;

    setLoading(true);
    setSubmitError('');

    // Build FormData — omit empty optional text fields
    const formData = new FormData();
    formData.append('business_name', fields.business_name.trim());
    formData.append('contact_name', fields.contact_name.trim());
    formData.append('email', fields.email.trim());
    formData.append('phone', fields.phone.trim());
    formData.append('address', fields.address.trim());
    if (fields.amazon_store_url.trim()) formData.append('amazon_store_url', fields.amazon_store_url.trim());
    if (fields.message.trim()) formData.append('message', fields.message.trim());
    formData.append('certificate', certificateFile);

    try {
      await submitWholesaleApplication(formData);
      setSuccess(true);
      setCertificate(null);
      if (certificateRef.current) certificateRef.current.value = '';
      if (onSuccess) onSuccess();
    } catch (err) {
      const status = err?.response?.status;
      const data = err?.response?.data;
      if (status === 429) {
        setSubmitError("You're submitting too fast — please wait a minute and try again.");
      } else if (status === 400 && data?.message) {
        setSubmitError(data.message);
      } else {
        setSubmitError('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }

  if (success) {
    return (
      <div className="form-success">
        <span className="form-success-icon" role="img" aria-label="Success">&#10003;</span>
        <h3>Application Received!</h3>
        <p>
          Thanks — your wholesale application has been received. Our team will respond within 24–48 hours.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate>
      {submitError && (
        <div className="form-error-banner" role="alert">
          {submitError}
        </div>
      )}

      {/* Row 1: Business Name + Contact Name */}
      <div className="form-grid">
        <div className="form-row">
          <label className="field-label" htmlFor="ws-business_name">
            Business Name <span className="req">*</span>
          </label>
          <input
            id="ws-business_name"
            name="business_name"
            type="text"
            className={`field-input${errors.business_name ? ' field-invalid' : ''}`}
            value={fields.business_name}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Acme Retail LLC"
            autoComplete="organization"
          />
          {errors.business_name && (
            <span className="field-error" role="alert">{errors.business_name}</span>
          )}
        </div>

        <div className="form-row">
          <label className="field-label" htmlFor="ws-contact_name">
            Contact Name <span className="req">*</span>
          </label>
          <input
            id="ws-contact_name"
            name="contact_name"
            type="text"
            className={`field-input${errors.contact_name ? ' field-invalid' : ''}`}
            value={fields.contact_name}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="Jane Smith"
            autoComplete="name"
          />
          {errors.contact_name && (
            <span className="field-error" role="alert">{errors.contact_name}</span>
          )}
        </div>
      </div>

      {/* Row 2: Email + Phone */}
      <div className="form-grid">
        <div className="form-row">
          <label className="field-label" htmlFor="ws-email">
            Email <span className="req">*</span>
          </label>
          <input
            id="ws-email"
            name="email"
            type="email"
            className={`field-input${errors.email ? ' field-invalid' : ''}`}
            value={fields.email}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="jane@example.com"
            autoComplete="email"
          />
          {errors.email && (
            <span className="field-error" role="alert">{errors.email}</span>
          )}
        </div>

        <div className="form-row">
          <label className="field-label" htmlFor="ws-phone">
            Phone <span className="req">*</span>
          </label>
          <input
            id="ws-phone"
            name="phone"
            type="tel"
            className={`field-input${errors.phone ? ' field-invalid' : ''}`}
            value={fields.phone}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="(555) 000-0000"
            autoComplete="tel"
          />
          {errors.phone && (
            <span className="field-error" role="alert">{errors.phone}</span>
          )}
        </div>
      </div>

      {/* Row 3: Address (full-width) */}
      <div className="form-row">
        <label className="field-label" htmlFor="ws-address">
          Address <span className="req">*</span>
        </label>
        <textarea
          id="ws-address"
          name="address"
          className={`field-input${errors.address ? ' field-invalid' : ''}`}
          value={fields.address}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder="123 Market St, Springfield, IL 62704"
          rows={2}
          autoComplete="street-address"
        />
        {errors.address && (
          <span className="field-error" role="alert">{errors.address}</span>
        )}
      </div>

      {/* Row 4: Amazon Store URL (single full-width — Seller ID removed) */}
      <div className="form-row">
        <label className="field-label" htmlFor="ws-amazon_store_url">
          Amazon Store URL
        </label>
        <input
          id="ws-amazon_store_url"
          name="amazon_store_url"
          type="url"
          className="field-input"
          value={fields.amazon_store_url}
          onChange={handleChange}
          placeholder="https://www.amazon.com/..."
        />
      </div>

      {/* Row 4: Resell Certificate upload */}
      <div className="form-row">
        <label className="field-label" htmlFor="ws-certificate">
          Resell Certificate (PDF or Image) <span className="req">*</span>
        </label>
        <div className="file-input-wrapper">
          <label
            htmlFor="ws-certificate"
            className={`file-input-label${errors.certificate ? ' file-input-label--invalid' : ''}`}
          >
            <span className="file-input-btn">Choose File</span>
            <span className="file-input-name">
              {certificateFile ? certificateFile.name : 'No file chosen'}
            </span>
          </label>
          <input
            id="ws-certificate"
            ref={certificateRef}
            type="file"
            accept="application/pdf,image/jpeg,image/png,.pdf,.jpg,.jpeg,.png"
            className="file-input-hidden"
            onChange={handleCertificateChange}
          />
        </div>
        {errors.certificate && (
          <span className="field-error" role="alert">{errors.certificate}</span>
        )}
      </div>

      {/* Row 5: Message */}
      <div className="form-row">
        <label className="field-label" htmlFor="ws-message">
          Message
        </label>
        <textarea
          id="ws-message"
          name="message"
          className="field-input"
          value={fields.message}
          onChange={handleChange}
          placeholder="Tell us about your business and what products you're looking for…"
          rows={4}
        />
      </div>

      <div className="form-submit-row">
        <Button
          variant="primary"
          type="submit"
          disabled={loading}
          className={loading ? 'btn-submitting' : ''}
        >
          {loading ? 'Submitting…' : 'Submit Application'}
        </Button>
      </div>
    </form>
  );
}

export default WholesaleForm;
